import datePart from './date/date_part';
import formats from './date/format';
import timezone from 'mex_components/app/scripts/date/timezone';

export default class ClassTimes {
  constructor(meetings) {
    this.meetings = meetings;
    this.weeklySchedule = new Map();
    this._buildList();
  }

  // extend our meetings array with formatted values
  // build a list of weekly class times
  _buildList() {
    // items API formats meetings as {start_time: , end_time: }
    // whereas elasticSearch API formats them as {start: , end: }
    this.meetings.forEach((meeting) => {
      meeting.shortDay = datePart.weekday(
        new Date(meeting.start || meeting.start_time),
        'short'
      );
      meeting.longDay = datePart.weekday(
        new Date(meeting.start || meeting.start_time),
        'long'
      );
      meeting.startTime = formats.shortTime(
        new Date(meeting.start || meeting.start_time)
      );
      meeting.endTime = formats.shortTime(
        new Date(meeting.end || meeting.end_time)
      );
      meeting.timeZone = timezone.shorterName();
      meeting.key = `${meeting.shortDay};${meeting.startTime}`;
      this.weeklySchedule.set(meeting.key, meeting);
    });
  }

  weekly() {
    return Array.from(this.weeklySchedule.values());
  }

  /**
   * Converts schedule into condensed weekly array in the user's browser time
   *
   * @returns array of the following:
   *   days: comma-separated short days in the format 'Mon, Tue, Wed'
   *   daysRange: range of days in the format 'Mon-Wed' or 'Mon, Wed-Fri'
   *   times: start time in the format '10:00 AM (PT)'
   *   timesRange: time range in the format `5:30 AM - 7:30 AM (PT)`
   */
  condensedWeekly() {
    let condensed = new Map();

    // collapse weekly based on time
    this.weeklySchedule.forEach((meeting) => {
      const times = `${meeting.startTime} (${meeting.timeZone})`;
      const timesRange = `${meeting.startTime} - ${meeting.endTime} (${meeting.timeZone})`;

      if (condensed.has(times)) {
        condensed.set(times, {
          days: condensed.get(times).days.concat(meeting.shortDay),
          times: times,
          timesRange: timesRange
        });
      } else {
        condensed.set(times, {
          days: [meeting.shortDay],
          times: times,
          timesRange: timesRange
        });
      }
    });

    // return array of formatted values
    return Array.from(condensed).map((el) => ({
      days: el[1].days.join(', '),
      daysRange: _formatDaysRange(el[1].days),
      times: el[1].times,
      timesRange: el[1].timesRange
    }));
  }
}

/**
 * Converts an array of days into a string of day ranges
 *
 * @param {Array} days Array of days, eg. ['Mon', 'Tue', 'Thu']
 * @returns Pretty string of day ranges, eg. 'Mon-Tue, Thu'
 */
const _formatDaysRange = (days) => {
  if (!days || !Array.isArray(days)) return '';

  const dayLookup = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // convert days into numbers we can sort
  const dayNumbers = days.map((day) => dayLookup.indexOf(day)).sort();

  // convert the number array into an array of ranges, convert them
  // back into day strings and combine them into the final string
  return _convertToRanges(dayNumbers)
    .map((range) => {
      return range
        .map((dayNumber) => {
          return dayLookup[dayNumber];
        })
        .join('-');
    })
    .join(', ');
};

/**
 * Converts an array of non-sorted numbers into an array of ranges
 *
 * @param {Array} numbers Array of numbers, eg. [3, 4, 6, 1, 2]
 * @returns Array of ranges, eg. [[1, 2], [3, 4], [6]]
 */
const _convertToRanges = (numbers) => {
  let ranges = [[numbers[0], numbers[0]]];
  let lastIndex = 0;

  for (var i = 1; i < numbers.length; i++) {
    if (numbers[i] - ranges[lastIndex][1] === 1) {
      // update the end range if current value is one away from next value
      ranges[lastIndex][1] = numbers[i];
    } else {
      // otherwise, add a new range
      ranges[++lastIndex] = [numbers[i], numbers[i]];
    }
  }

  // remove isolated numbers that aren't in a range
  return ranges.map((range) => [...new Set(range)]);
};
